import { useRef, useEffect, RefObject } from 'react';

export interface SwipeAction {
  id: string;
  label: string;
  icon: string;
  color: string;
  backgroundColor: string;
  action: () => void;
}

export interface SwipeGestureConfig {
  leftAction?: SwipeAction;
  rightAction?: SwipeAction;
  threshold?: number; // Distance minimale pour déclencher l'action (en pixels)
  maxThreshold?: number; // Distance maximale pour l'effet visuel
}

interface SwipeState {
  startX: number;
  currentX: number;
  startY: number;
  currentY: number;
  isDragging: boolean;
  direction: 'left' | 'right' | null;
  isHorizontalSwipe: boolean;
}

export const useSwipeGestures = (
  config: SwipeGestureConfig,
  elementRef: RefObject<HTMLElement>
) => {
  const swipeState = useRef<SwipeState>({
    startX: 0,
    currentX: 0,
    startY: 0,
    currentY: 0,
    isDragging: false,
    direction: null,
    isHorizontalSwipe: false
  });

  const {
    leftAction,
    rightAction,
    threshold = 100,
    maxThreshold = 200
  } = config;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let animationId: number | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      // Ne pas interférer avec les scrolls si pas de touches configurées
      if (!leftAction && !rightAction) return;
      
      const touch = e.touches[0];
      swipeState.current = {
        startX: touch.clientX,
        currentX: touch.clientX,
        startY: touch.clientY,
        currentY: touch.clientY,
        isDragging: true,
        direction: null,
        isHorizontalSwipe: false
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!swipeState.current.isDragging) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - swipeState.current.startX;
      const deltaY = touch.clientY - swipeState.current.startY;
      
      swipeState.current.currentX = touch.clientX;
      swipeState.current.currentY = touch.clientY;
      
      // Détecter si c'est un geste horizontal ou vertical
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      
      // Seuil pour détecter la direction du geste (minimum 15px de mouvement)
      if (absDeltaX > 15 || absDeltaY > 15) {
        // Si le mouvement horizontal est significativement plus important que le vertical, c'est un swipe
        if (absDeltaX > absDeltaY * 2.0 && absDeltaX > 20) {
          swipeState.current.isHorizontalSwipe = true;
          swipeState.current.direction = deltaX > 0 ? 'right' : 'left';
          
          // Empêcher le scroll vertical seulement si c'est clairement un swipe horizontal
          e.preventDefault();
          e.stopPropagation();
          
          // Appliquer la transformation visuelle
          const clampedDelta = Math.max(-maxThreshold, Math.min(maxThreshold, deltaX));
          element.style.transform = `translateX(${clampedDelta}px)`;
          element.style.transition = 'none';

          // Afficher l'indicateur d'action
          updateActionIndicator(deltaX);
        } else if (absDeltaY > absDeltaX) {
          // C'est clairement un scroll vertical, arrêter le swipe et permettre le scroll
          swipeState.current.isDragging = false;
          swipeState.current.isHorizontalSwipe = false;
          return;
        }
      }
    };

    const handleTouchEnd = () => {
      if (!swipeState.current.isDragging) return;

      const deltaX = swipeState.current.currentX - swipeState.current.startX;
      const absDeltaX = Math.abs(deltaX);

      // Réinitialiser la transformation avec animation
      element.style.transition = 'transform 0.3s ease-out';
      element.style.transform = 'translateX(0px)';

      // Exécuter l'action seulement si c'était un swipe horizontal et si le seuil est atteint
      if (swipeState.current.isHorizontalSwipe && absDeltaX >= threshold) {
        setTimeout(() => {
          if (deltaX > 0 && rightAction) {
            rightAction.action();
          } else if (deltaX < 0 && leftAction) {
            leftAction.action();
          }
        }, 50); // Petit délai pour éviter les conflits
      }

      // Nettoyer les indicateurs d'action
      clearActionIndicators();

      // Reset complet de l'état
      swipeState.current = {
        startX: 0,
        currentX: 0,
        startY: 0,
        currentY: 0,
        isDragging: false,
        direction: null,
        isHorizontalSwipe: false
      };
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Support pour desktop/souris
      swipeState.current = {
        startX: e.clientX,
        currentX: e.clientX,
        startY: e.clientY,
        currentY: e.clientY,
        isDragging: true,
        direction: null,
        isHorizontalSwipe: false
      };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!swipeState.current.isDragging) return;

      const deltaX = e.clientX - swipeState.current.startX;
      const deltaY = e.clientY - swipeState.current.startY;
      swipeState.current.currentX = e.clientX;
      swipeState.current.currentY = e.clientY;

      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Détecter si c'est un mouvement horizontal intentionnel
      if (absDeltaX > 10 || absDeltaY > 10) {
        if (absDeltaX > absDeltaY * 1.5) {
          swipeState.current.isHorizontalSwipe = true;
          swipeState.current.direction = deltaX > 0 ? 'right' : 'left';

          const clampedDelta = Math.max(-maxThreshold, Math.min(maxThreshold, deltaX));
          element.style.transform = `translateX(${clampedDelta}px)`;
          element.style.transition = 'none';

          updateActionIndicator(deltaX);
        }
      }
    };

    const handleMouseUp = () => {
      if (!swipeState.current.isDragging) return;

      const deltaX = swipeState.current.currentX - swipeState.current.startX;
      const absDeltaX = Math.abs(deltaX);

      element.style.transition = 'transform 0.3s ease-out';
      element.style.transform = 'translateX(0px)';

      // Exécuter l'action seulement si c'était un swipe horizontal
      if (swipeState.current.isHorizontalSwipe && absDeltaX >= threshold) {
        if (deltaX > 0 && rightAction) {
          rightAction.action();
        } else if (deltaX < 0 && leftAction) {
          leftAction.action();
        }
      }

      clearActionIndicators();
      swipeState.current.isDragging = false;
      swipeState.current.isHorizontalSwipe = false;
    };

    const updateActionIndicator = (deltaX: number) => {
      // Ne montrer l'indicateur que si c'est un swipe horizontal
      if (!swipeState.current.isHorizontalSwipe) return;
      
      const action = deltaX > 0 ? rightAction : leftAction;
      if (!action) return;

      const progress = Math.min(Math.abs(deltaX) / threshold, 1);
      
      // Créer ou mettre à jour l'indicateur d'action
      let indicator = element.querySelector('.swipe-indicator') as HTMLElement;
      
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'swipe-indicator';
        indicator.style.cssText = `
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          pointer-events: none;
          z-index: 10;
          transition: opacity 0.2s ease;
        `;
        element.appendChild(indicator);
      }

      indicator.style.backgroundColor = action.backgroundColor;
      indicator.style.color = action.color;
      indicator.innerHTML = action.icon;
      indicator.style.opacity = progress.toString();
      
      if (deltaX > 0) {
        indicator.style.right = '20px';
        indicator.style.left = 'auto';
      } else {
        indicator.style.left = '20px';
        indicator.style.right = 'auto';
      }
    };

    const clearActionIndicators = () => {
      const indicator = element.querySelector('.swipe-indicator');
      if (indicator) {
        indicator.remove();
      }
    };

    // Ajouter les event listeners avec options plus compatibles
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    element.addEventListener('mousedown', handleMouseDown, { passive: true });
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp, { passive: true });

    // Cleanup
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      
      element.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      clearActionIndicators();
    };
  }, [leftAction, rightAction, threshold, maxThreshold, elementRef]);

  return swipeState.current;
};
